import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts'
import { CheckCircle2, Factory, Layers, XCircle } from 'lucide-react'
import { BAR_RADIUS, CHART, gridProps, tooltipProps, xAxisProps, yAxisProps } from '@/lib/chart'
import type { SlaRow, ThroughputReportRow } from '@/types'
import Card, { SectionLabel } from '@/components/ui/Card'
import StatTile from '@/components/ui/StatTile'
import { ChartBox } from '@/components/ui/Chart'
import { Table, Row, Cell, EmptyRow } from '@/components/ui/Table'

const icon = 'size-5'

export default function ThroughputTab({ data, sla, range }: { data: ThroughputReportRow[]; sla?: SlaRow[]; range: { from: string; to: string } }) {
  const jobs = data.reduce((sum, r) => sum + r.jobs_completed, 0)
  const pieces = data.reduce((sum, r) => sum + r.pieces, 0)
  const busiest = [...data].sort((a, b) => b.pieces - a.pieces)[0]

  return (
    <>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatTile label="Stages completed" value={jobs} icon={<CheckCircle2 className={icon} />} tone="success" hint="jobs passed through a section" />
        <StatTile label="Pieces completed" value={pieces} icon={<Layers className={icon} />} tone="pending" />
        <StatTile label="Busiest section" value={busiest && busiest.pieces > 0 ? busiest.department : '-'} icon={<Factory className={icon} />} tone="primary" hint={busiest && busiest.pieces > 0 ? `${busiest.pieces} pcs` : undefined} className="col-span-2 lg:col-span-1" />
      </div>

      <Card className="mt-6 min-w-0">
        <SectionLabel>Pieces completed per section</SectionLabel>
        <ChartBox>
          <BarChart data={data}>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey="department" {...xAxisProps} tick={{ fontSize: 10, fill: CHART.tick }} interval={0} />
            <YAxis {...yAxisProps} />
            <Tooltip {...tooltipProps} />
            <Bar dataKey="pieces" name="Pieces" fill={CHART.primary} radius={BAR_RADIUS} />
          </BarChart>
        </ChartBox>
      </Card>

      <SectionLabel className="mt-6">Throughput per section ({range.from} to {range.to})</SectionLabel>
      <Table columns={['Department', 'Jobs completed', 'Pieces']}>
        {data.map((row) => (
          <Row key={row.department}>
            <Cell className="font-semibold">{row.department}</Cell>
            <Cell>{row.jobs_completed}</Cell>
            <Cell className="font-semibold">{row.pieces}</Cell>
          </Row>
        ))}
        {data.length === 0 && <EmptyRow colSpan={3}>No departments configured.</EmptyRow>}
      </Table>

      <SectionLabel className="mt-6">SLA vs actual (all time, from QR scan timestamps)</SectionLabel>
      <Table columns={['Department', 'SLA (days)', 'Jobs measured', 'Avg actual (days)', 'On track']}>
        {(sla?.length ?? 0) === 0 && <EmptyRow colSpan={5}>No SLA data yet.</EmptyRow>}
        {sla?.map((row) => (
          <Row key={row.department}>
            <Cell className="font-semibold">{row.department}</Cell>
            <Cell>{row.sla_days}</Cell>
            <Cell>{row.jobs_measured}</Cell>
            <Cell>{row.avg_actual_days ?? '-'}</Cell>
            <Cell>
              {row.on_track === null ? (
                <span className="text-muted">no data</span>
              ) : row.on_track ? (
                <span className="inline-flex items-center gap-1 font-semibold text-status-success">
                  <CheckCircle2 className="size-4" /> Yes
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 font-semibold text-status-danger">
                  <XCircle className="size-4" /> Exceeding SLA
                </span>
              )}
            </Cell>
          </Row>
        ))}
      </Table>
    </>
  )
}
